import { useLocation } from "react-router-dom";
import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import {
  DashboardOutlined,
  UserOutlined,
  MenuUnfoldOutlined,
  LoadingOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  SettingFilled,
  MessageFilled,
} from "@ant-design/icons";
import {
  Menu,
  Button,
  Layout,
  Drawer,
  Spin,
  Row,
  Card,
  Col,
  Avatar,
} from "antd";
import type { MenuProps } from "antd";
import { AppContext } from "src/context";
import has_permission from "src/common/utils/permission";

import AIIMG from "src/assets/img/ai.gif";

import "./css/NavbarVertical.css";

import { useNavigate } from "react-router-dom";
import apiRequest from "src/common/utils/apiRequest";
import modules from "src/common/utils/modules";
import coreFunctions from "src/common/utils/coreFunctions";

const { Sider } = Layout;

interface NavbarVerticalProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const convertIcons = (items: MenuProps["items"]): MenuProps["items"] => {
  return items?.map((item: any) => {
    const newItem: any = {
      ...item,
      icon: coreFunctions.renderIcon(item.icon as string),
    };

    if (item.children && Array.isArray(item.children)) {
      newItem.children = convertIcons(item.children);
    }

    return newItem;
  });
};

const NavbarVertical: React.FC<NavbarVerticalProps> = ({
  collapsed,
  setCollapsed,
}) => {
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [menuModuleItems, setMenuModuleItems] = useState<MenuProps["items"]>(
    []
  );
  const [menuItems, setMenuItems] = useState<MenuProps["items"]>([]);
  const [drawerMenuItems, setDrawerMenuItems] = useState<MenuProps["items"]>([
    {
      key: "user-management",
      label: "User Management",
      icon: <UserOutlined />,
      children: [
        { key: "modules/setting/users", label: "Users" },
        { key: "modules/setting/users/role", label: "Roles" },
        { key: "modules/setting/users/department", label: "Departments" },
      ],
    },
    {
      key: "prdeifined-notes",
      label: "Predefined Notes",
      icon: <UserOutlined />,
      children: [
        {
          key: "modules/setting/predifined-notes/client-notes",
          label: "Client Notes",
        },
        {
          key: "modules/setting/predifined-notes/terms-conditions",
          label: "Terms And Condition",
        },
      ],
    },
  ]);
  const [loadMenu, setLoadMenu] = useState(false);
  const [activeNav, setActiveNav] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const location = useLocation();
  const navigate = useNavigate();

  const { state } = useContext(AppContext);
  const isMobileDevice = coreFunctions.isMobileDevice();

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  const onOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    if (e.key === "settings") {
      setIsDrawerVisible(true);
    } else {
      const event = window.event as MouseEvent;
      const isNewTab =
        (event && (event.ctrlKey || event.metaKey)) || event.button === 1;
      const url = `/${e.key}`;

      if (isNewTab) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    }
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeMenu = async () => {
      setLoadMenu(true);

      const updatedModules = await Promise.all(
        modules.map(async (route) => {
          try {
            const module = await import(`src/modules/${route.key}/routes`);
            const children = module.default.filter(
              (child: any) => child.navigation
            );

            return {
              ...route,
              ...(children.length > 0 ? { children } : {}),
            };
          } catch (err) {
            return route;
          }
        })
      );

      const cleanModules = updatedModules.map(({ children, ...module }) => ({
        ...module,
        ...(children?.length
          ? {
              children: children.map(
                ({ element, ...child }: { element: JSX.Element }) => child
              ),
            }
          : {}),
      }));

      const API_REQUEST = new apiRequest("initialize/menu", true, true);

      await API_REQUEST.sendAndCache(
        "menu_cache",
        60000,
        { menu: cleanModules },
        (response: { status: string; data: MenuProps["items"] }) => {
          if (isMounted && response.status === "success") {
            const iconifiedMenu = convertIcons(response.data);
            setMenuModuleItems(iconifiedMenu);
          }
        }
      );

      if (isMounted) setLoadMenu(false);
    };

    initializeMenu();

    return () => {
      isMounted = false;
    };
  }, [state.permission_changed]);

  useEffect(() => {
    setMenuItems([
      {
        key: "main",
        type: "group",
        label: "MAIN",
        children: [
          {
            key: "dashboard",
            icon: <DashboardOutlined />,
            label: "Dashboard",
          },
          // {
          //   key: "fortified_intelligence",
          //   icon: <Avatar size={16} src={AIIMG} />,
          //   label: "Fortified Intelligence",
          // },
        ],
      },
      { type: "divider" },
      {
        key: "sections",
        type: "group",
        label: "SECTIONS",
        children: menuModuleItems,
      },
    ]);
  }, [menuModuleItems]);

  useEffect(() => {
    const segments = location.pathname.replace(/^\/+/, "").split("/");

    if (segments[0] === "modules" && segments.length > 1) {
      setActiveNav(location.pathname.replace(/^\/+/, ""));

      setOpenKeys([segments[1]]);
    }
  }, [location.pathname]);

  return (
    <Sider
      collapsible
      collapsed={false}
      onCollapse={toggleCollapse}
      trigger={null}
      width={isMobileDevice ? "100%" : collapsed ? 80 : 256}
      className="navbar-sider"
    >
      {loadMenu ? (
        <Card style={{ textAlign: "center", width: "100%" }}>
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </Card>
      ) : (
        <motion.div
          key="navbar-vertical-list"
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
        >
          <Row
            style={{
              height: "100vh",
              overflowY: "scroll",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              paddingBottom: "50px",
            }}
          >
            <Menu
              onClick={handleMenuClick}
              defaultSelectedKeys={[activeNav || "dashboard"]}
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              mode="inline"
              inlineCollapsed={collapsed}
              items={menuItems}
              className="navbar-menu"
            />
            <div
              className="navbar-footer"
              style={{ position: "fixed", bottom: 0 }}
            >
              <Row style={{ backgroundColor: "#fff" }}>
                {!isMobileDevice && (
                  <Col>
                    <Button
                      type="text"
                      onClick={toggleCollapse}
                      className="navbar-toggle"
                    >
                      {collapsed ? (
                        <MenuUnfoldOutlined />
                      ) : (
                        <MenuFoldOutlined />
                      )}
                    </Button>
                  </Col>
                )}
                {has_permission("global", "setting", "edit") && (
                  <Col>
                    <Button
                      type="text"
                      onClick={() => {
                        setIsDrawerVisible(!isDrawerVisible);
                      }}
                      className="navbar-toggle"
                    >
                      {isDrawerVisible ? (
                        <SettingFilled />
                      ) : (
                        <SettingOutlined />
                      )}
                    </Button>
                  </Col>
                )}
              </Row>

              <Drawer
                title="Setup"
                placement="left"
                onClose={closeDrawer}
                open={isDrawerVisible}
                width={280}
                closable
                className="navbar-sider"
              >
                <motion.div
                  key="navbar-vertical-list"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu
                    mode="inline"
                    items={drawerMenuItems}
                    className="navbar-menu"
                    onClick={(e) => {
                      navigate(`/${e.key}`);
                      closeDrawer();
                    }}
                    style={{ borderInlineEnd: "none" }}
                  />
                </motion.div>
              </Drawer>
            </div>
          </Row>
        </motion.div>
      )}
    </Sider>
  );
};

export default NavbarVertical;
